import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, photoUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Send, Ban, Flag, LogOut, Sparkles, BadgeCheck } from "lucide-react";

export default function MatchDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [m, setM] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("Harassment");
  const [reportDesc, setReportDesc] = useState("");
  const [ice, setIce] = useState([]);
  const bottomRef = useRef(null);

  const loadMatch = async () => {
    try { const { data } = await api.get(`/matches/${id}`); setM(data); }
    catch { toast.error("Match not found"); nav("/matches"); }
  };
  const loadMsgs = async () => {
    try { const { data } = await api.get(`/matches/${id}/messages`); setMsgs(data); } catch {}
  };

  useEffect(() => { loadMatch(); loadMsgs(); }, [id]);
  useEffect(() => { const t = setInterval(loadMsgs, 3000); return () => clearInterval(t); }, [id]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  // Fetch AI icebreakers when chat is empty
  useEffect(() => {
    if (!m) return;
    if (msgs.length > 0) { setIce([]); return; }
    if (ice.length > 0) return;
    (async () => {
      try { const { data } = await api.get(`/matches/${id}/icebreakers`); setIce(data.items || []); } catch {}
    })();
  }, [m, msgs.length, id, ice.length]);

  const sendIce = async (t) => {
    try { await api.post(`/matches/${id}/messages`, { text: t }); setIce([]); loadMsgs(); }
    catch (e) { toast.error("Send failed"); }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try { await api.post(`/matches/${id}/messages`, { text }); setText(""); loadMsgs(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
  };

  const endConn = async () => {
    await api.post(`/matches/${id}/end`);
    toast.success("Connection ended");
    nav("/dashboard");
  };
  const block = async () => {
    if (!m?.other) return;
    await api.post(`/users/${m.other.id}/block`);
    toast.success("User blocked");
    nav("/dashboard");
  };
  const report = async () => {
    try {
      await api.post(`/users/${m.other.id}/report`, { reason: reportReason, description: reportDesc });
      setReportOpen(false);
      toast.success("Report submitted");
    } catch (e) { toast.error("Report failed"); }
  };

  if (!m) return <div className="min-h-screen bg-dc-gradient flex items-center justify-center text-purple-200">Loading…</div>;
  const o = m.other;

  return (
    <div className="min-h-screen bg-dc-gradient flex flex-col">
      <nav className="flex items-center gap-3 p-4 max-w-2xl w-full mx-auto">
        <Link to="/matches" className="text-sm text-purple-200">←</Link>
        <div className="w-10 h-10 rounded-full overflow-hidden bg-black/40">
          {o?.photo_ids?.[0] && <img src={photoUrl(o.photo_ids[0])} className="w-full h-full object-cover" alt="" />}
        </div>
        <div className="flex-1">
          <div className="font-bold flex items-center gap-1.5">
            {o?.display_name}
            {o?.verified && (
              <span data-testid="verified-badge" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-500/20 border border-sky-400/40 text-sky-200 text-[10px] font-bold uppercase tracking-widest shadow-[0_0_14px_rgba(56,189,248,0.4)]">
                <BadgeCheck className="w-3 h-3" /> Verified
              </span>
            )}
          </div>
          <div className="text-[11px] text-purple-200/70">{o?.age} · {o?.city}</div>
        </div>
        <button data-testid="match-report" onClick={() => setReportOpen(true)} className="text-purple-200 hover:text-rose-300" title="Report"><Flag className="w-4 h-4" /></button>
        <button data-testid="match-block" onClick={block} className="text-purple-200 hover:text-rose-300" title="Block"><Ban className="w-4 h-4" /></button>
        <button data-testid="match-end" onClick={endConn} className="text-purple-200 hover:text-rose-300" title="End"><LogOut className="w-4 h-4" /></button>
      </nav>

      <main className="flex-1 max-w-2xl w-full mx-auto px-4 pb-4 flex flex-col">
        <section className="glass rounded-3xl p-4 mb-3">
          <div className="flex gap-3">
            {(o?.photo_ids || []).slice(0, 3).map((pid) => (
              <img key={pid} src={photoUrl(pid)} className="w-24 h-24 object-cover rounded-xl" alt="" />
            ))}
          </div>
          <p className="text-sm text-purple-100/90 mt-3">{o?.bio || "No bio yet."}</p>
        </section>

        <section className="glass rounded-3xl flex-1 flex flex-col p-3 min-h-[300px]">
          <div className="flex-1 overflow-y-auto space-y-2">
            {msgs.map((msg) => (
              <div key={msg.id} className={`flex ${msg.sender_id === user.id ? "justify-end" : "justify-start"}`}>
                <div data-testid={`chat-msg-${msg.id}`} className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${msg.sender_id === user.id ? "bg-gradient-to-r from-pink-500 to-fuchsia-600 text-white" : "bg-purple-900/60 text-white"}`}>
                  {msg.text}
                </div>
              </div>
            ))}
            {msgs.length === 0 && ice.length > 0 && (
              <div data-testid="icebreakers" className="mt-2">
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-fuchsia-300 mb-2">
                  <Sparkles className="w-3.5 h-3.5" /> AI icebreakers · tap to send
                </div>
                <div className="space-y-2">
                  {ice.map((t, i) => (
                    <button data-testid={`icebreaker-${i}`} key={i} onClick={() => sendIce(t)} className="w-full text-left bg-fuchsia-500/10 hover:bg-fuchsia-500/20 border border-fuchsia-400/30 rounded-2xl px-3 py-2 text-sm text-fuchsia-100">
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          <form onSubmit={send} className="mt-3 flex gap-2">
            <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" className="flex-1 bg-black/40 border border-purple-500/30 rounded-2xl px-4 py-2.5 text-white outline-none" />
            <button data-testid="chat-send" type="submit" className="btn-cta rounded-2xl px-4"><Send className="w-4 h-4" /></button>
          </form>
        </section>
      </main>

      {reportOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
          <div className="glass-strong rounded-3xl p-6 max-w-sm w-full">
            <h3 className="font-display text-xl font-bold">Report user</h3>
            <select value={reportReason} onChange={(e) => setReportReason(e.target.value)} className="mt-4 w-full bg-black/40 border border-purple-500/30 rounded-xl px-3 py-2">
              {["Fake Profile", "Harassment", "Spam", "Inappropriate Behaviour", "Other"].map((r) => <option key={r}>{r}</option>)}
            </select>
            <textarea value={reportDesc} onChange={(e) => setReportDesc(e.target.value)} placeholder="Describe (optional)" rows={3} className="mt-3 w-full bg-black/40 border border-purple-500/30 rounded-xl px-3 py-2" />
            <div className="mt-4 flex gap-2">
              <button onClick={() => setReportOpen(false)} className="flex-1 py-2.5 rounded-xl bg-purple-900/60">Cancel</button>
              <button data-testid="report-submit" onClick={report} className="flex-1 py-2.5 rounded-xl btn-cta">Submit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
